import { CircularProgress, Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';

import { ChatConversationListItem } from './ChatConversationListItem';

export interface ChatConversationListItemData {
  id: string;
  /**
   * Whether this row offers the delete control. Defaults to true; set false for
   * conversations the current user doesn't own, such as ones shared across
   * everyone with access. Only consulted when `onDeleteConversation` is given.
   */
  isDeletable?: boolean;
  /** Pre-formatted relative time label, e.g. "2 hours ago". */
  timestamp: string;
  title: string;
}

interface ChatConversationListProps {
  conversations: ChatConversationListItemData[];
  emptyDescription: string;
  emptyTitle: string;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  onDeleteConversation?: (conversationId: string) => void;
  onLoadMore: () => void;
  onSelectConversation: (conversationId: string) => void;
}

/**
 * Presentational conversation-history list for chat surfaces: loading and
 * empty states, one row per conversation, and a "Load more" control for
 * cursor pagination. Data fetching belongs to the feature-specific caller.
 */
export function ChatConversationList({
  conversations,
  emptyDescription,
  emptyTitle,
  hasMore,
  loading,
  loadingMore,
  onDeleteConversation,
  onLoadMore,
  onSelectConversation,
}: ChatConversationListProps) {
  if (loading && conversations.length === 0) {
    return (
      <Stack
        sx={{ alignItems: 'center', flexGrow: 1, justifyContent: 'center' }}
      >
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (conversations.length === 0) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          flexGrow: 1,
          gap: 1,
          justifyContent: 'center',
          px: 3,
        }}
      >
        <Typography variant="title-s" textAlign="center">
          {emptyTitle}
        </Typography>
        <Typography variant="body-xs" textAlign="center">
          {emptyDescription}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
      {conversations.map((conversation) => (
        <ChatConversationListItem
          key={conversation.id}
          onClick={() => onSelectConversation(conversation.id)}
          onDelete={
            onDeleteConversation && conversation.isDeletable !== false
              ? () => onDeleteConversation(conversation.id)
              : undefined
          }
          timestamp={conversation.timestamp}
          title={conversation.title}
        />
      ))}
      {hasMore && (
        <Stack sx={{ alignItems: 'center', p: 2 }}>
          <Button
            variant="secondary"
            size="sm"
            loading={loadingMore}
            onClick={onLoadMore}
          >
            Load more
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
